import { TextField, InputAdornment, IconButton } from '@mui/material';
import {
  Person,
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  PersonAdd,
  Badge,
} from '@mui/icons-material';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';

import Button from '../components/formElements/Button';
import { useAuth } from '../context/AuthContext';

interface FormValues {
  name: string;
  surname: string;
  email: string;
  password: string;
  confirmPassword: string;
}

function RegisterForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  const { register } = useAuth();
  const navigate = useNavigate();

  // Watch values for animations and validation
  const watchedValues = {
    name: watch('name'),
    surname: watch('surname'),
    email: watch('email'),
    password: watch('password'),
  };

  // Password strength indicator
  const getPasswordStrength = (password: string) => {
    if (!password) return { strength: 0, label: '' };

    // Calculate strength based on length and character types
    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;

    // Return strength value and corresponding label
    const labels = ['Słabe', 'Średnie', 'Dobre', 'Mocne'];
    return {
      strength,
      label: strength > 0 ? labels[strength - 1] : '',
    };
  };

  const passwordStrength = getPasswordStrength(watchedValues.password);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const onSubmit = async (data: FormValues) => {
    setIsLoading(true);
    const toastId = toast.loading('Rejestracja w toku...', {
      position: 'top-right',
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: 0,
      theme: 'colored',
    });

    try {
      await register(data.name, data.surname, data.email, data.password);
      toast.update(toastId, {
        render: 'Zarejestrowano pomyślnie!',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      setTimeout(() => {
        navigate('/logowanie');
      }, 1000);
    } catch (error: Error | any) {
      console.error('Registration failed', error);
      toast.update(toastId, {
        render: error.message,
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
      setIsLoading(false);
    }
  };

  // Animacje
  const formVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 24,
      },
    },
  };

  return (
    <motion.form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col space-y-5 items-stretch w-full"
      variants={formVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <motion.div variants={itemVariants}>
          <Controller
            name="name"
            control={control}
            rules={{
              required: 'Imię jest wymagane',
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Imię"
                variant="outlined"
                fullWidth
                error={!!errors.name}
                helperText={errors.name ? String(errors.name.message) : ''}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person
                        color={
                          errors.name
                            ? 'error'
                            : watchedValues.name
                              ? 'primary'
                              : 'action'
                        }
                      />
                    </InputAdornment>
                  ),
                }}
                className="bg-white rounded-lg"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: '2px',
                    },
                    '& fieldset': {
                      borderRadius: '0.5rem',
                    },
                  },
                }}
              />
            )}
          />
        </motion.div>

        <motion.div variants={itemVariants}>
          <Controller
            name="surname"
            control={control}
            rules={{
              required: 'Nazwisko jest wymagane',
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Nazwisko"
                variant="outlined"
                fullWidth
                error={!!errors.surname}
                helperText={
                  errors.surname ? String(errors.surname.message) : ''
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Badge
                        color={
                          errors.surname
                            ? 'error'
                            : watchedValues.surname
                              ? 'primary'
                              : 'action'
                        }
                      />
                    </InputAdornment>
                  ),
                }}
                className="bg-white rounded-lg"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: '2px',
                    },
                    '& fieldset': {
                      borderRadius: '0.5rem',
                    },
                  },
                }}
              />
            )}
          />
        </motion.div>
      </div>

      <motion.div variants={itemVariants}>
        <Controller
          name="email"
          control={control}
          rules={{
            required: 'Email jest wymagany',
            pattern: {
              value: /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/i,
              message: 'Niepoprawny adres email',
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              label="Email"
              variant="outlined"
              fullWidth
              error={!!errors.email}
              helperText={errors.email ? String(errors.email.message) : ''}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email
                      color={
                        errors.email
                          ? 'error'
                          : watchedValues.email
                            ? 'primary'
                            : 'action'
                      }
                    />
                  </InputAdornment>
                ),
              }}
              className="bg-white rounded-lg"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: '2px',
                  },
                  '& fieldset': {
                    borderRadius: '0.5rem',
                  },
                },
              }}
            />
          )}
        />
      </motion.div>

      <motion.div variants={itemVariants}>
        <Controller
          name="password"
          control={control}
          rules={{
            required: 'Hasło jest wymagane',
            pattern: {
              value: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$/,
              message:
                'Hasło musi zawierać co najmniej 8 znaków, jedną małą i wielką literę oraz cyfrę',
            },
          }}
          render={({ field }) => (
            <>
              <TextField
                {...field}
                type={showPassword ? 'text' : 'password'}
                label="Hasło"
                variant="outlined"
                fullWidth
                error={!!errors.password}
                helperText={
                  errors.password ? String(errors.password.message) : ''
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock
                        color={
                          errors.password
                            ? 'error'
                            : watchedValues.password
                              ? 'primary'
                              : 'action'
                        }
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                className="bg-white rounded-lg"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: '2px',
                    },
                    '& fieldset': {
                      borderRadius: '0.5rem',
                    },
                  },
                }}
              />
              {watchedValues.password && !errors.password && (
                <div className="mt-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-grow h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${
                          passwordStrength.strength === 1
                            ? 'bg-red-500'
                            : passwordStrength.strength === 2
                              ? 'bg-yellow-500'
                              : passwordStrength.strength === 3
                                ? 'bg-green-500'
                                : passwordStrength.strength === 4
                                  ? 'bg-green-600'
                                  : ''
                        }`}
                        style={{ width: `${passwordStrength.strength * 25}%` }}
                      />
                    </div>
                    <span
                      className={`text-xs ${
                        passwordStrength.strength === 1
                          ? 'text-red-600'
                          : passwordStrength.strength === 2
                            ? 'text-yellow-600'
                            : passwordStrength.strength === 3
                              ? 'text-green-600'
                              : passwordStrength.strength === 4
                                ? 'text-green-700'
                                : ''
                      }`}
                    >
                      {passwordStrength.label}
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        />
      </motion.div>

      <motion.div variants={itemVariants}>
        <Controller
          name="confirmPassword"
          control={control}
          rules={{
            required: 'Powtórzenie hasła jest wymagane',
            validate: (value) =>
              value === watchedValues.password || 'Hasła muszą się zgadzać',
          }}
          render={({ field }) => (
            <TextField
              {...field}
              type={showConfirmPassword ? 'text' : 'password'}
              label="Powtórz hasło"
              variant="outlined"
              fullWidth
              error={!!errors.confirmPassword}
              helperText={
                errors.confirmPassword
                  ? String(errors.confirmPassword.message)
                  : ''
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock
                      color={
                        errors.confirmPassword
                          ? 'error'
                          : field.value
                            ? 'primary'
                            : 'action'
                      }
                    />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowConfirmPassword}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              className="bg-white rounded-lg"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: '2px',
                  },
                  '& fieldset': {
                    borderRadius: '0.5rem',
                  },
                },
              }}
            />
          )}
        />
      </motion.div>

      <motion.div
        variants={itemVariants}
        className="pt-2"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <Button
          type="submit"
          colorTheme="primary"
          disabled={isLoading}
          className="w-full h-12 !rounded-lg transition-all duration-300 relative overflow-hidden"
        >
          <motion.div
            className="flex items-center justify-center gap-2 w-full"
            initial={false}
            animate={isLoading ? { x: -10 } : { x: 0 }}
          >
            <PersonAdd />
            <span>Zarejestruj się</span>
          </motion.div>

          {isLoading && (
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            </motion.div>
          )}
        </Button>
      </motion.div>

      <motion.div
        variants={itemVariants}
        className="text-center text-xs text-gray-500 mt-4"
      >
        * Wszystkie pola są wymagane
      </motion.div>
    </motion.form>
  );
}

export default RegisterForm;
